import { useParams, useOutletContext, useNavigate } from 'react-router-dom';
import { useTranslations } from '../i18n';
import { theme, styles } from '../lib/theme';
import { ReminderItem } from '../components/cars/ReminderItem';
import { useReminders } from '../hooks/useCarData';

type RemindersListProps = {
  carId: string;
  isPublic?: boolean;
};

export function RemindersList({ carId }: RemindersListProps) {
  const { slug } = useParams();
  const outletContext = useOutletContext<any>() || {};
  const { t } = useTranslations();
  const navigate = useNavigate();
  
  const { data: reminders = [], isLoading } = useReminders(carId);
  const sortedReminders = [...reminders].sort((a: any, b: any) => {
    // 1. Move action_required (triggered) to TOP
    if (a.action_required && !b.action_required) return -1;
    if (!a.action_required && b.action_required) return 1;

    // 2. Next by remind_date (standard)
    const dateA = new Date(a.remind_date).getTime();
    const dateB = new Date(b.remind_date).getTime();
    if (dateA !== dateB) return dateA - dateB;

    // 3. Fallback to newest first
    return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
  });

  const standaloneReminders = sortedReminders.filter((r: any) => !r.note_id && !r.part_id);
  const linkedReminders = sortedReminders.filter((r: any) => r.note_id || r.part_id);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 font-primary">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4" />
        <p className="text-gray-500 font-semibold italic">{t.common.loading}...</p>
      </div>
    );
  }

  const handleReminderClick = (reminder: any) => {
    const targetSlug = slug;
    const prefix = outletContext.isAdminView ? '/admin' : '';
    
    if (reminder.note_id) {
      navigate(`${prefix}/${targetSlug}/notes/${reminder.note_id}`);
    } else if (reminder.part_id) {
      navigate(`${prefix}/${targetSlug}/parts/${reminder.part_id}`);
    } else {
      navigate(`${prefix}/${targetSlug}/reminders/${reminder.id}`);
    }
  };

  return (
    <div className={`${theme.spacing.pagePadding} pb-32 pt-4 relative`}>
      <div className="space-y-6">
        {/* Category: Standalone Reminders */}
        {standaloneReminders.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between px-1 py-1">
              <h3 className={theme.typography.sectionTitle}>
                {t.reminders.standalone || 'Atsevišķi atgādinājumi'}
              </h3>
            </div>

            <div className={`${styles.card} shadow-sm divide-y ${theme.colors.divider}`}>
              {standaloneReminders.map((reminder: any) => (
                <ReminderItem
                  key={reminder.id}
                  reminder={reminder}
                  onClick={() => handleReminderClick(reminder)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Category: Linked Reminders */}
        <div className="space-y-3">
          <div className="flex items-center justify-between px-1 py-1">
            <h3 className={theme.typography.sectionTitle}>
              {t.reminders.embedded || 'Piesaistīti atgādinājumi'}
            </h3>
          </div>

          {linkedReminders.length === 0 ? (
            <div className={`${styles.card} p-8 text-center shadow-sm opacity-60`}>
              <p className="text-gray-400 dark:text-gray-300 text-sm font-medium italic">
                {t.reminders.noReminders || 'Nav piesaistītu atgādinājumu'}
              </p>
            </div>
          ) : (
            <div className={`${styles.card} shadow-sm divide-y ${theme.colors.divider}`}>
              {linkedReminders.map((reminder: any) => (
                <ReminderItem
                  key={reminder.id}
                  reminder={reminder}
                  onClick={() => handleReminderClick(reminder)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
